import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Home as HomeIcon,
  Car,
  Wallet,
  Briefcase,
  Loader2,
  CheckCircle2,
  Phone,
} from "lucide-react";
import { api, BUSINESS } from "../lib/api";
import { toast } from "sonner";
import { SEO } from "../components/SEO";

const loanTypes = [
  { id: "home", label: "Home Loan", icon: HomeIcon, desc: "Buy, build, refinance, invest" },
  { id: "car", label: "Car / Asset", icon: Car, desc: "Cars, utes, equipment" },
  { id: "personal", label: "Personal Loan", icon: Wallet, desc: "Renovations, consolidation" },
  { id: "business", label: "Business Loan", icon: Briefcase, desc: "Working capital, commercial" },
];

const amountRanges = {
  home: ["Up to $500k", "$500k – $800k", "$800k – $1.2m", "$1.2m+"],
  car: ["Up to $20k", "$20k – $40k", "$40k – $80k", "$80k+"],
  personal: ["Up to $10k", "$10k – $30k", "$30k – $50k", "$50k+"],
  business: ["Up to $100k", "$100k – $500k", "$500k – $1m", "$1m+"],
};

export default function Quote() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    loan_type: "",
    loan_amount: "",
    employment_status: "",
    full_name: "",
    email: "",
    phone: "",
    postcode: "",
    message: "",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => {
    if (step === 1 && !data.loan_type) return toast.error("Pick a loan type");
    if (step === 2 && !data.loan_amount) return toast.error("Pick a loan amount");
    setStep((s) => s + 1);
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.full_name || !data.email || !data.phone) {
      return toast.error("Please fill your name, email and phone.");
    }
    setLoading(true);
    try {
      await api.post("/quotes", data);
      setSubmitted(true);
      toast.success("Quote request received!");
    } catch (err) {
      toast.error("Submission failed. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-24">
        <div
          data-testid="quote-success"
          className="rounded-3xl bg-white border border-emerald-100 p-10 md:p-14 text-center shadow-sm"
        >
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
          <h1 className="mt-5 text-3xl md:text-4xl font-bold text-emerald-950 tracking-tight">
            You’re in good hands.
          </h1>
          <p className="mt-4 text-slate-600 max-w-lg mx-auto">
            Thanks {data.full_name.split(" ")[0]}. A broker will call you at{" "}
            <span className="num font-semibold text-emerald-900">{data.phone}</span>{" "}
            within one business day. We’ll come prepared with lender options.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={BUSINESS.phoneHref}
              data-testid="quote-success-call"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-emerald-950 font-semibold hover:bg-emerald-400"
            >
              <Phone className="h-4 w-4" /> Call us now
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-emerald-200 text-emerald-900 font-semibold hover:bg-emerald-50"
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO
        path="/quote"
        title="Get a Free Quote — Home, Car, Personal & Business Loans"
        description="Get a free, no-obligation finance quote from Sixty Finance. Home loans, car & asset finance, personal & business loans. A broker calls you back the same day."
      />
      <section className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">Free quote</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold tracking-tighter max-w-3xl">
            Get your finance options in <span className="text-emerald-300">under 2 minutes.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-emerald-100/80">
            No obligation. Won’t affect your credit score. A real broker calls you back the same day.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 md:px-8 py-14">
        {/* Steps */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1">
              <div
                className={`h-1.5 rounded-full ${
                  step >= n ? "bg-emerald-500" : "bg-emerald-100"
                }`}
              />
              <div className="mt-2 text-xs font-semibold text-emerald-900 num">
                Step {n}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-3xl bg-white border border-gray-100 p-8 md:p-10 shadow-sm">
          {step === 1 && (
            <div data-testid="quote-step-1">
              <h2 className="text-2xl font-bold text-emerald-950">What are you financing?</h2>
              <p className="mt-2 text-sm text-slate-500">Pick the closest match.</p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {loanTypes.map((t) => {
                  const Icon = t.icon;
                  const active = data.loan_type === t.id;
                  return (
                    <button
                      type="button"
                      key={t.id}
                      data-testid={`loan-type-${t.id}`}
                      onClick={() => set("loan_type", t.id)}
                      className={`text-left rounded-2xl border p-5 hover:-translate-y-0.5 transition-transform ${
                        active
                          ? "bg-emerald-900 text-white border-emerald-900"
                          : "bg-white text-emerald-950 border-gray-200 hover:border-emerald-400"
                      }`}
                    >
                      <Icon className={`h-6 w-6 ${active ? "text-emerald-300" : "text-emerald-700"}`} />
                      <div className="mt-3 font-semibold">{t.label}</div>
                      <div className={`text-xs mt-1 ${active ? "text-emerald-200" : "text-slate-500"}`}>{t.desc}</div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-8 flex justify-end">
                <button
                  onClick={next}
                  data-testid="quote-next-1"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-900 text-white font-semibold text-sm hover:bg-emerald-800"
                >
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="quote-step-2">
              <h2 className="text-2xl font-bold text-emerald-950">How much do you need?</h2>
              <p className="mt-2 text-sm text-slate-500">A rough range is fine — we’ll refine on the call.</p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {(amountRanges[data.loan_type] || []).map((r) => {
                  const active = data.loan_amount === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      data-testid={`amount-${r}`}
                      onClick={() => set("loan_amount", r)}
                      className={`rounded-xl border p-4 text-sm font-semibold text-left ${
                        active
                          ? "bg-emerald-900 text-white border-emerald-900"
                          : "bg-white text-emerald-950 border-gray-200 hover:border-emerald-400"
                      }`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6">
                <label className="block">
                  <span className="text-sm font-semibold text-emerald-950">Employment status</span>
                  <select
                    data-testid="employment-status"
                    value={data.employment_status}
                    onChange={(e) => set("employment_status", e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-emerald-950 focus:border-emerald-400 focus:ring-0 outline-none"
                  >
                    <option value="">Select…</option>
                    <option value="full-time">Full-time employed</option>
                    <option value="part-time">Part-time / Casual</option>
                    <option value="self-employed">Self-employed</option>
                    <option value="contract">Contract</option>
                    <option value="other">Other</option>
                  </select>
                </label>
              </div>

              <div className="mt-8 flex justify-between">
                <button
                  onClick={back}
                  data-testid="quote-back-2"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-emerald-200 text-emerald-900 font-semibold text-sm hover:bg-emerald-50"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button
                  onClick={next}
                  data-testid="quote-next-2"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-900 text-white font-semibold text-sm hover:bg-emerald-800"
                >
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <form onSubmit={submit} data-testid="quote-step-3">
              <h2 className="text-2xl font-bold text-emerald-950">Where should we send options?</h2>
              <p className="mt-2 text-sm text-slate-500">Your details are kept private. No spam, ever.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Input label="Full name" name="full_name" value={data.full_name} onChange={(v) => set("full_name", v)} required />
                <Input label="Email" name="email" type="email" value={data.email} onChange={(v) => set("email", v)} required />
                <Input label="Phone" name="phone" type="tel" value={data.phone} onChange={(v) => set("phone", v)} required />
                <Input label="Postcode (optional)" name="postcode" value={data.postcode} onChange={(v) => set("postcode", v)} />
              </div>
              <label className="mt-4 block">
                <span className="text-sm font-semibold text-emerald-950">Anything else? (optional)</span>
                <textarea
                  data-testid="quote-message"
                  rows={4}
                  value={data.message}
                  onChange={(e) => set("message", e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-emerald-950 focus:border-emerald-400 focus:ring-0 outline-none"
                  placeholder="E.g. best time to call, specific lender, etc."
                />
              </label>

              <div className="mt-8 flex justify-between items-center gap-4">
                <button
                  type="button"
                  onClick={back}
                  data-testid="quote-back-3"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-emerald-200 text-emerald-900 font-semibold text-sm hover:bg-emerald-50"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-emerald-950 font-bold text-sm hover:bg-emerald-400 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  {loading ? "Sending…" : "Get my free quote"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Input({ label, name, type = "text", value, onChange, required }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-emerald-950">
        {label} {required && <span className="text-emerald-600">*</span>}
      </span>
      <input
        data-testid={`quote-${name}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-emerald-950 focus:border-emerald-400 focus:ring-0 outline-none"
      />
    </label>
  );
}
